import React from 'react';
import { MapPin, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PerkTags from '../../components/PerkTag';
import { resolveStampIcon } from '../../components/stamps/registry';
import { LocalLink } from '../../i18n/useLocalizedNav';
import { categoryKey } from '../../content/catalog';
import { locationHero } from '../../utils/images';
import type { Location } from './types';

const LocationCard: React.FC<{ location: Location }> = ({ location }) => {
  const { t } = useTranslation();
  // Icon of the stamp this venue grants (the location → stamp connection).
  const renderStampIcon = resolveStampIcon(
    location.stampName,
    location.name,
    location.category,
  );

  return (
    <LocalLink
      to={`/locations/${location.slug}`}
      className="bg-white rounded-2xl border border-warm-200/70 overflow-hidden flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all group"
    >
      <div className="relative h-48 bg-warm-200 overflow-hidden">
        <PerkTags
          discountPercent={location.discountPercent}
          hasGift={location.hasGift}
          className="absolute top-3 left-3 z-10"
        />
        {(() => {
          const hero = locationHero(location.slug, location.imageCount);
          return hero ? (
            <img
              src={hero}
              alt={location.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              backgroundImage:
                'repeating-linear-gradient(135deg, transparent, transparent 14px, rgba(255,255,255,0.5) 14px, rgba(255,255,255,0.5) 28px)',
            }}
          >
            <span className="px-3 py-1 bg-white/80 rounded-md font-heading font-bold text-[10px] tracking-[0.15em] text-warm-500 uppercase">
              {t('loc_card_photo')} · {location.name}
            </span>
          </div>
          );
        })()}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <p className="font-heading font-bold text-[10px] tracking-[0.15em] text-warm-500 uppercase">
          {t(categoryKey(location.category))}
        </p>

        <div className="flex items-start justify-between gap-3 mt-1">
          <h3 className="font-display text-2xl text-warm-800 leading-tight group-hover:text-brand-accent transition-colors">
            {location.name}
          </h3>
          {/* Connected stamp */}
          <div
            className="w-10 h-10 shrink-0 bg-brand-primary/15 rounded-full flex items-center justify-center text-brand-secondary"
            title={t('loc_card_stamp_tooltip', { name: location.stampName })}
          >
            {renderStampIcon({ width: 22, height: 22 })}
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-warm-500 text-sm mt-2">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">
            {[location.address, location.region].filter(Boolean).join(' · ')}
          </span>
        </div>

        <div className="border-t border-dashed border-warm-200 mt-4 pt-4 flex items-center justify-between">
          <p className="text-sm text-warm-500">
            {t('loc_card_earn')}{' '}
            <span className="font-bold text-warm-800">
              {location.stampName}
            </span>
          </p>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-warm-100 group-hover:bg-brand-primary text-warm-800 group-hover:text-brand-secondary font-heading font-bold text-sm rounded-lg transition-colors">
            {t('loc_card_open')}
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </LocalLink>
  );
};

export default LocationCard;
